import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ConfirmModal } from "@/components/ConfirmModal";
import { CycleModal } from "@/components/CycleModal";
import {
  CycleTargetModal,
  type TargetCandidate,
} from "@/components/CycleTargetModal";
import { useBodyMeasurementEntries } from "@/hooks/useBodyMeasurements";
import { useAllCycles, useCycle } from "@/hooks/useCycles";
import { useSettings } from "@/hooks/useSettings";
import { useAllWeeks } from "@/hooks/useWeeks";
import {
  BODY_MEASUREMENT_CATALOG,
  formatMeasurementValue,
} from "@/lib/body-measurements";
import {
  attachWeeks,
  baselineWeek,
  type CycleData,
  type CycleTarget,
  type CycleWeek,
  cycleDateRangeLabel,
  cycleLastMonday,
  cycleWeeks,
  cycleWeeksLabel,
  isCycleEnded,
  judgeMeasurementTarget,
  judgePointsTarget,
  type MeasurementTarget,
  measuredChange,
  measurementAverages,
  type PointsTarget,
  pointsTargetCell,
  requiredWeeksOf,
  samletPercent,
  settledWeekCount,
  summaryRowCell,
  summaryRowColumns,
  type TargetStatus,
  targetCandidateSummaryRows,
  targetCandidateWidgets,
  type WeekCell,
} from "@/lib/cycles";
import { addDays, weekRangeLabel } from "@/lib/days";
import { refLabel, scorableRefs } from "@/lib/ref-scoring";
import {
  archiveCycle,
  deleteCycle,
  reopenCycle,
  updateCycle,
} from "@/lib/repos/cycles";
import type { WidgetConfig } from "@/lib/schema";

const STATUS_LABEL: Record<TargetStatus, string> = {
  met: "Nådd",
  "not-met": "Ikke nådd",
  "on-track": "I rute",
  behind: "Under kravet",
  open: "Pågår",
  missing: "Mangler målinger",
};

function unitOf(target: MeasurementTarget): string {
  return (
    BODY_MEASUREMENT_CATALOG.find((t) => t.key === target.typeKey)?.unit ?? ""
  );
}

function measurementLabel(target: MeasurementTarget): string {
  return (
    BODY_MEASUREMENT_CATALOG.find((t) => t.key === target.typeKey)?.label ??
    target.typeKey
  );
}

/** "+1,5" / "−2" — a change always carries its sign. */
function formatSigned(value: number): string {
  const formatted = formatMeasurementValue(Math.abs(value));
  if (formatted === "0") return "0";
  return `${value < 0 ? "−" : "+"}${formatted}`;
}

function pointsTargetDefinition(
  target: PointsTarget,
  cycle: CycleData,
): string {
  if (target.rule === "samlet") {
    return `Minst ${target.requiredPercent} % · samlet`;
  }
  return `Minst ${target.requiredPercent} % i ${requiredWeeksOf(target, cycle)} av ${cycle.length} uker`;
}

function PercentCell({
  cell,
  bar,
  testId,
}: {
  cell: WeekCell;
  /** The percentage a week must reach to read as good. */
  bar: number;
  testId: string;
}) {
  if (cell.kind === "future") {
    return <td data-testid={testId} />;
  }
  if (cell.kind === "ikke-med") {
    return (
      <td className="td-zero" data-testid={testId}>
        ikke med
      </td>
    );
  }
  const percent = Math.round(cell.percent);
  const reached = cell.percent >= bar;
  const className =
    cell.kind === "hittil"
      ? "td-pct td-hittil"
      : `td-pct ${reached ? "up" : "down"}`;
  return (
    <td className={className} data-testid={testId} data-kind={cell.kind}>
      {percent}%{cell.kind === "hittil" ? <small> hittil</small> : null}
    </td>
  );
}

/** A points column's Samlet figure over its Avgjort weeks, or — before any. */
function ResultPercentCell({
  percent,
  bar,
  testId,
}: {
  percent: number | null;
  bar: number;
  testId: string;
}) {
  if (percent === null) return <td data-testid={testId}>—</td>;
  return (
    <td
      className={`td-pct ${percent >= bar ? "up" : "down"}`}
      data-testid={testId}
    >
      {Math.round(percent)}%
    </td>
  );
}

function TargetItem({
  target,
  cycle,
  weeks,
  label,
  onEdit,
  onDelete,
  measurementJudgement,
}: {
  target: CycleTarget;
  cycle: CycleData;
  weeks: CycleWeek[];
  label: string;
  onEdit: () => void;
  onDelete: () => void;
  measurementJudgement: ReturnType<typeof judgeMeasurementTarget> | null;
}) {
  const today = new Date();
  let definition: string;
  let result: string;
  let status: TargetStatus;

  if (target.kind === "points") {
    const cells = weeks.map((w) => pointsTargetCell(target, w, today));
    const judgement = judgePointsTarget(target, cycle, cells);
    definition = pointsTargetDefinition(target, cycle);
    status = judgement.status;
    if (target.rule === "samlet") {
      result =
        judgement.percent === null
          ? "Ingen avgjorte uker ennå"
          : `${Math.round(judgement.percent)} %${judgement.remaining > 0 ? " så langt" : ""}`;
    } else {
      result = `${judgement.passed} av ${judgement.required} bestått, ${judgement.remaining} igjen`;
    }
  } else {
    const judgement = measurementJudgement;
    const unit = unitOf(target);
    definition = `${formatSigned(target.wantedChange)} ${unit} over syklusen`;
    status = judgement?.status ?? "missing";
    if (
      !judgement ||
      judgement.change === null ||
      judgement.percent === null ||
      !judgement.from ||
      !judgement.to
    ) {
      result = "Trenger målinger i minst to uker";
    } else {
      result = `${formatSigned(judgement.change)} ${unit} (${Math.round(judgement.percent)} %) · uke ${judgement.from.weekNumber} → uke ${judgement.to.weekNumber}`;
    }
  }

  return (
    <li className="cycle-target" data-testid={`cycle-target-${target.id}`}>
      <div className="cycle-target-main">
        <span className="cycle-target-label">{label}</span>
        <span className="cycle-target-definition">{definition}</span>
        <span
          className="cycle-target-result"
          data-testid={`cycle-target-result-${target.id}`}
        >
          {result}
        </span>
      </div>
      <span
        className={`cycle-target-status status-${status}`}
        data-testid={`cycle-target-status-${target.id}`}
        data-status={status}
      >
        {STATUS_LABEL[status]}
      </span>
      <div className="cycle-target-actions">
        <button
          type="button"
          className="icon-btn"
          aria-label="Rediger mål"
          onClick={onEdit}
          data-testid={`edit-target-${target.id}`}
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label="Fjern mål"
          onClick={onDelete}
          data-testid={`delete-target-${target.id}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
}

type TargetModalState = { target?: CycleTarget } | null;

export function Cycle() {
  const params = useParams<{ cycleId: string }>();
  const cycleId = decodeURIComponent(params.cycleId ?? "");
  const navigate = useNavigate();
  const { cycle, loading } = useCycle(cycleId);
  const { cycles } = useAllCycles();
  const { weeks: allWeeks } = useAllWeeks();
  const { settings } = useSettings();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [targetModal, setTargetModal] = useState<TargetModalState>(null);

  const baseline = cycle ? baselineWeek(cycle.data) : null;
  const { entries } = useBodyMeasurementEntries(
    baseline?.monday ?? null,
    cycle ? addDays(cycleLastMonday(cycle.data), 6) : null,
  );

  if (loading) return <p>Laster…</p>;
  if (!cycle || !baseline) {
    return <p data-testid="cycle-not-found">Syklusen finnes ikke.</p>;
  }

  const data = cycle.data;
  const today = new Date();
  const weeks = attachWeeks(cycleWeeks(data), allWeeks);
  const widgets: WidgetConfig[] = targetCandidateWidgets(
    settings.widgets,
    weeks,
  );
  const labelOf = (ref: string) => refLabel(ref, widgets) ?? "(slettet widget)";
  const offeredRows = targetCandidateSummaryRows(settings.widgets, weeks);
  const showRowInstanceLabels =
    new Set(offeredRows.map((r) => r.instanceId)).size > 1;
  const rowLabel = (rowId: string) => {
    const row = offeredRows.find((r) => r.rowId === rowId);
    if (!row) return "(slettet rad)";
    const name = row.name || "(uten navn)";
    return showRowInstanceLabels ? `${name} · ${row.instanceLabel}` : name;
  };
  const targetLabel = (t: PointsTarget) =>
    t.summaryRowId !== undefined
      ? rowLabel(t.summaryRowId)
      : labelOf(t.ref ?? "");

  const candidateRefs = widgets.flatMap(scorableRefs);
  // A target's own widget stays choosable when editing it, even if that
  // instance no longer scores.
  for (const t of data.targets) {
    if (t.kind === "points" && t.ref !== undefined) {
      if (!candidateRefs.includes(t.ref)) candidateRefs.push(t.ref);
    }
  }
  const candidates: TargetCandidate[] = [
    ...offeredRows.map((row) => ({
      group: "row" as const,
      value: `row:${row.rowId}`,
      label: rowLabel(row.rowId),
    })),
    ...candidateRefs.map((ref) => ({
      group: "widget" as const,
      value: `widget:${ref}`,
      label: labelOf(ref),
    })),
  ];

  const pointsTargets = data.targets.filter(
    (t): t is PointsTarget => t.kind === "points",
  );
  // A row followed by a target is shown once, as the target's column.
  const targetedRows = new Set(
    pointsTargets.flatMap((t) =>
      t.summaryRowId === undefined ? [] : [t.summaryRowId],
    ),
  );
  const summaryColumns = summaryRowColumns(weeks).filter(
    (c) => !targetedRows.has(c.rowId),
  );
  const showInstanceLabels =
    new Set(summaryColumns.map((c) => c.instanceId)).size > 1;
  const averages = measurementAverages(entries, [baseline, ...weeks]);
  const ended = isCycleEnded(data, today);
  const weeksWithCount = `${cycleWeeksLabel(data)} (${data.length === 1 ? "1 uke" : `${data.length} uker`})`;

  const removeTarget = (id: string) =>
    updateCycle(cycle.id, (prev) => ({
      ...prev,
      targets: prev.targets.filter((t) => t.id !== id),
    }));

  const deleteAndLeave = async () => {
    setDeleteOpen(false);
    await deleteCycle(cycle.id);
    navigate("/");
  };

  return (
    <div data-screen-label="Syklus" className="cycle-page">
      <p className="week-cycle-label">Syklus</p>
      <h1 className="page-title" data-testid="cycle-title">
        {data.name.trim() || weeksWithCount}
      </h1>
      <p className="cycle-meta" data-testid="cycle-meta">
        {data.name.trim() ? `${weeksWithCount} · ` : ""}
        {cycleDateRangeLabel(data)}
        {ended ? " · avsluttet" : ""}
        {data.archived ? " · arkivert" : ""}
      </p>
      {data.note.trim() ? (
        <p className="cycle-note" data-testid="cycle-note">
          {data.note}
        </p>
      ) : null}

      <div className="page-toolbar cycle-toolbar">
        <button
          type="button"
          className="btn ghost"
          onClick={() => setEditOpen(true)}
          data-testid="cycle-edit-button"
        >
          Rediger
        </button>
        {data.archived ? (
          <button
            type="button"
            className="btn ghost"
            onClick={() => reopenCycle(cycle.id)}
            data-testid="reopen-cycle-button"
          >
            Gjenåpne
          </button>
        ) : (
          <button
            type="button"
            className={ended ? "btn-primary" : "btn ghost"}
            onClick={() => archiveCycle(cycle.id)}
            data-testid="archive-cycle-button"
          >
            Arkiver
          </button>
        )}
      </div>

      <section className="cycle-section">
        <h2 className="cycle-section-title">Mål</h2>
        {data.targets.length === 0 ? (
          <p className="hint" data-testid="cycle-targets-empty">
            Ingen mål ennå.
          </p>
        ) : (
          <ul className="cycle-targets" data-testid="cycle-targets">
            {data.targets.map((t) => (
              <TargetItem
                key={t.id}
                target={t}
                cycle={data}
                weeks={weeks}
                label={
                  t.kind === "points" ? targetLabel(t) : measurementLabel(t)
                }
                measurementJudgement={
                  t.kind === "measurement"
                    ? judgeMeasurementTarget(t, data, entries, today)
                    : null
                }
                onEdit={() => setTargetModal({ target: t })}
                onDelete={() => removeTarget(t.id)}
              />
            ))}
          </ul>
        )}
        <button
          type="button"
          className="btn ghost icon-btn"
          onClick={() => setTargetModal({})}
          data-testid="add-target-button"
        >
          <Plus size={16} />
          Legg til mål
        </button>
      </section>

      <section className="cycle-section">
        <h2 className="cycle-section-title">Uker</h2>
        <div className="week-matrix">
          <table data-testid="cycle-grid">
            <thead>
              <tr>
                <th className="th-group">Uke</th>
                {pointsTargets.map((t) => (
                  <th key={t.id} data-testid={`cycle-grid-target-${t.id}`}>
                    {targetLabel(t)}
                  </th>
                ))}
                {summaryColumns.map((c, i) => (
                  <th
                    key={c.rowId}
                    className={i === 0 ? "th-divider" : undefined}
                    data-testid={`cycle-grid-summary-${c.rowId}`}
                  >
                    {c.name || "(uten navn)"}
                    {showInstanceLabels ? (
                      <small className="th-sub">{c.instanceLabel}</small>
                    ) : null}
                  </th>
                ))}
                {BODY_MEASUREMENT_CATALOG.map((type, i) => (
                  <th
                    key={type.key}
                    className={i === 0 ? "th-divider" : undefined}
                  >
                    {type.label} ({type.unit})
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="row-baseline" data-testid="cycle-baseline-row">
                <td className="td-label">
                  Utgangspunkt
                  <small>uke {baseline.weekNumber}</small>
                </td>
                {pointsTargets.map((t) => (
                  <td key={t.id} />
                ))}
                {summaryColumns.map((c, i) => (
                  <td
                    key={c.rowId}
                    className={i === 0 ? "td-divider" : undefined}
                  />
                ))}
                {BODY_MEASUREMENT_CATALOG.map((type, i) => {
                  const value = averages[type.key][0];
                  return (
                    <td
                      key={type.key}
                      className={i === 0 ? "td-divider" : undefined}
                      data-testid={`cycle-measurement-baseline-${type.key}`}
                    >
                      {value === null ? "" : formatMeasurementValue(value)}
                    </td>
                  );
                })}
              </tr>
              {weeks.map((w, index) => {
                const key = `${w.year}-${w.weekNumber}`;
                return (
                  <tr key={key} data-testid={`cycle-week-row-${key}`}>
                    <td className="td-label">
                      {w.week ? (
                        <Link
                          to={`/uke/${encodeURIComponent(w.week.id)}`}
                          data-testid={`cycle-week-link-${key}`}
                        >
                          Uke {w.weekNumber}
                        </Link>
                      ) : (
                        <>Uke {w.weekNumber}</>
                      )}
                      <small>{weekRangeLabel(w.monday)}</small>
                    </td>
                    {pointsTargets.map((t) => (
                      <PercentCell
                        key={t.id}
                        cell={pointsTargetCell(t, w, today)}
                        bar={t.requiredPercent}
                        testId={`cycle-cell-${t.id}-${key}`}
                      />
                    ))}
                    {summaryColumns.map((c) => (
                      <PercentCell
                        key={c.rowId}
                        cell={summaryRowCell(c.rowId, w, today)}
                        bar={100}
                        testId={`cycle-summary-cell-${c.rowId}-${key}`}
                      />
                    ))}
                    {BODY_MEASUREMENT_CATALOG.map((type, i) => {
                      const value = averages[type.key][index + 1];
                      return (
                        <td
                          key={type.key}
                          className={i === 0 ? "td-divider" : undefined}
                          data-testid={`cycle-measurement-cell-${type.key}-${key}`}
                        >
                          {value === null ? "" : formatMeasurementValue(value)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="row-total" data-testid="cycle-result-row">
                <td className="td-label">Resultat</td>
                {pointsTargets.map((t) => {
                  const cells = weeks.map((w) => pointsTargetCell(t, w, today));
                  const testId = `cycle-result-${t.id}`;
                  if (t.rule === "uke-for-uke") {
                    const settled = settledWeekCount(cells);
                    if (settled === 0)
                      return (
                        <td key={t.id} data-testid={testId}>
                          —
                        </td>
                      );
                    const { passed, status } = judgePointsTarget(
                      t,
                      data,
                      cells,
                    );
                    // Red only once the required count is out of reach.
                    return (
                      <td
                        key={t.id}
                        className={`td-pct ${status === "not-met" ? "down" : "up"}`}
                        data-testid={testId}
                      >
                        {passed} av {settled}
                      </td>
                    );
                  }
                  return (
                    <ResultPercentCell
                      key={t.id}
                      percent={samletPercent(cells)}
                      bar={t.requiredPercent}
                      testId={testId}
                    />
                  );
                })}
                {summaryColumns.map((c) => (
                  <ResultPercentCell
                    key={c.rowId}
                    percent={samletPercent(
                      weeks.map((w) => summaryRowCell(c.rowId, w, today)),
                    )}
                    bar={100}
                    testId={`cycle-result-summary-${c.rowId}`}
                  />
                ))}
                {BODY_MEASUREMENT_CATALOG.map((type, i) => {
                  const measured = measuredChange(
                    type.key,
                    data,
                    entries,
                    today,
                  );
                  return (
                    <td
                      key={type.key}
                      className={i === 0 ? "td-divider" : undefined}
                      data-testid={`cycle-result-measurement-${type.key}`}
                    >
                      {measured
                        ? `${formatSigned(measured.change)} · ${formatSigned(measured.perWeek)}/uke`
                        : "—"}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <div className="page-toolbar page-toolbar--danger-zone">
        <button
          type="button"
          className="btn danger icon-btn"
          onClick={() => setDeleteOpen(true)}
          data-testid="delete-cycle-button"
        >
          <Trash2 size={16} />
          Slett syklusen
        </button>
      </div>

      <ConfirmModal
        open={deleteOpen}
        title="Slett syklusen?"
        body="Syklusen og målene blir slettet. Ukene og målingene blir ikke rørt."
        confirmLabel="Slett syklusen"
        confirmIcon={<Trash2 size={16} />}
        onConfirm={deleteAndLeave}
        onCancel={() => setDeleteOpen(false)}
        data-testid="delete-cycle-confirm-modal"
      />
      {editOpen ? (
        <CycleModal
          cycles={cycles}
          cycle={cycle}
          onClose={() => setEditOpen(false)}
        />
      ) : null}
      {targetModal ? (
        <CycleTargetModal
          cycle={cycle}
          candidates={candidates}
          target={targetModal.target}
          onClose={() => setTargetModal(null)}
        />
      ) : null}
    </div>
  );
}
